export interface MarketplaceProduct {
  id: string
  title: string
  description: string
  type:
    | 'курс'
    | 'pdf'
    | 'вебинар'
    | 'марафон'
    | 'видеоурок'
    | 'чек-лист'
    | 'закрытый_клуб'
    | 'запись'
  category: string
  price: string
  priceNum: number
  authorId: string
  authorName: string
  authorAvatar: string
  status: 'draft' | 'moderation' | 'active' | 'hidden' | 'rejected' | 'archived'
  salesCount: number
  revenue: string
  rating: number
  reviewsCount: number
  coverColor: string
  createdAt: string
  updatedAt: string
  materials: { type: string; name: string; size?: string }[]
  targetAudience: string
  whatUserGets: string[]
  language: string
  items: ProductItem[]
}

export interface ProductItem {
  id: string
  type:
    | 'pdf'
    | 'file'
    | 'bonus'
    | 'consultation'
    | 'video'
    | 'audio'
    | 'checklist'
    | 'module'
  title: string
  description?: string
  fileUrl?: string
  fileSize?: string
  accessType: 'download' | 'stream' | 'booking' | 'link'
  sortOrder: number
  status: 'ready' | 'pending' | 'processing'
}

export interface MarketplaceOrder {
  id: string
  productId: string
  productTitle: string
  productType: string
  buyerId: string
  buyerName: string
  buyerEmail: string
  sellerId: string
  sellerName: string
  price: string
  priceNum: number
  platformCommission: number
  commissionAmount: string
  sellerPayout: string
  status:
    | 'pending_payment'
    | 'paid'
    | 'access_granted'
    | 'refunded'
    | 'canceled'
    | 'disputed'
  accessStatus: 'pending' | 'granted' | 'revoked'
  createdAt: string
  paymentMethod: string
}

export interface MarketplaceSeller {
  id: string
  name: string
  email: string
  productsCount: number
  totalSales: number
  totalRevenue: string
  balance: string
  paidOut: string
  rating: number
  complaintsCount: number
  status: 'active' | 'restricted' | 'blocked'
}

export interface MarketplacePayout {
  id: string
  sellerId: string
  sellerName: string
  amount: string
  commission: string
  status: 'pending' | 'available' | 'paid_out' | 'failed'
  createdAt: string
  transactionId?: string
}

export interface MarketplaceDispute {
  id: string
  orderId: string
  productId: string
  productTitle: string
  buyerId: string
  buyerName: string
  sellerId: string
  sellerName: string
  reason: string
  status: 'open' | 'in_review' | 'resolved_buyer' | 'resolved_seller' | 'closed'
  createdAt: string
  resolvedAt?: string
  adminComment?: string
  amount: string
}

export interface MarketplaceReview {
  id: string
  productId: string
  productTitle: string
  buyerId: string
  buyerName: string
  rating: number
  text: string
  createdAt: string
  status: 'published' | 'moderation' | 'hidden'
}

export interface MarketplaceNotificationLog {
  id: string
  type: string
  recipientType: 'astrologer' | 'user' | 'admin'
  recipientName: string
  message: string
  status: 'sent' | 'failed' | 'pending'
  sentAt: string
  channel: 'email' | 'internal' | 'sms'
}

export const initialMarketplaceProducts: MarketplaceProduct[] = [
  {
    id: 'PROD-001',
    title: 'Основы ведической астрологии: с нуля до первых прогнозов',
    description:
      'Полный базовый курс по Джйотиш. Научитесь читать натальную карту, понимать кармические задачи и делать точные прогнозы.',
    type: 'курс',
    category: 'Обучение',
    price: '15 000 ₽',
    priceNum: 15000,
    authorId: 'AST-1',
    authorName: 'Елена Смирнова',
    authorAvatar: 'ЕС',
    status: 'active',
    salesCount: 142,
    revenue: '2 130 000 ₽',
    rating: 4.9,
    reviewsCount: 38,
    coverColor: 'bg-indigo-100',
    createdAt: '12 Сен 2023',
    updatedAt: '05 Окт 2024',
    materials: [
      { type: 'video', name: 'Модуль 1: Введение (12 уроков)', size: '2.4 GB' },
      { type: 'pdf', name: 'Рабочая тетрадь студента', size: '15 MB' },
      { type: 'link', name: 'Доступ в закрытый чат Telegram' },
    ],
    targetAudience:
      'Новички, интересующиеся астрологией для себя и будущей профессии.',
    whatUserGets: [
      'Понимание своей карты',
      'Навык чтения чужих карт',
      'Сертификат о прохождении',
    ],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-001-1',
        type: 'video',
        title: 'Модуль 1: Введение (12 уроков)',
        description: 'Базовые понятия ведической астрологии',
        fileUrl: '/storage/prod001/module1.mp4',
        fileSize: '2.4 GB',
        accessType: 'stream',
        sortOrder: 1,
        status: 'ready',
      },
      {
        id: 'ITEM-001-2',
        type: 'pdf',
        title: 'Рабочая тетрадь студента',
        description: 'Задания и упражнения к каждому модулю',
        fileUrl: '/storage/prod001/workbook.pdf',
        fileSize: '15 MB',
        accessType: 'download',
        sortOrder: 2,
        status: 'ready',
      },
      {
        id: 'ITEM-001-3',
        type: 'bonus',
        title: 'Доступ в закрытый чат Telegram',
        description: 'Общение с другими студентами и преподавателем',
        accessType: 'link',
        sortOrder: 3,
        status: 'ready',
      },
      {
        id: 'ITEM-001-4',
        type: 'consultation',
        title: 'Разбор вашей карты (30 мин)',
        description:
          'Индивидуальная консультация с астрологом по вашей натальной карте',
        accessType: 'booking',
        sortOrder: 4,
        status: 'ready',
      },
      {
        id: 'ITEM-001-5',
        type: 'checklist',
        title: 'Чек-лист: Первый разбор карты',
        description: 'Пошаговый алгоритм для самостоятельного разбора',
        fileUrl: '/storage/prod001/checklist.pdf',
        fileSize: '2 MB',
        accessType: 'download',
        sortOrder: 5,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-002',
    title: 'Гайд: Как пережить транзит Сатурна без потерь',
    description:
      'Практическое руководство по прохождению сложных периодов Сатурна (Саде-Сати, возвращение Сатурна).',
    type: 'pdf',
    category: 'Практикумы',
    price: '1 490 ₽',
    priceNum: 1490,
    authorId: 'AST-2',
    authorName: 'Анна Коваль',
    authorAvatar: 'АК',
    status: 'active',
    salesCount: 356,
    revenue: '530 440 ₽',
    rating: 4.8,
    reviewsCount: 89,
    coverColor: 'bg-slate-200',
    createdAt: '01 Фев 2024',
    updatedAt: '01 Фев 2024',
    materials: [
      { type: 'pdf', name: 'Гайд_Транзиты_Сатурна.pdf', size: '8 MB' },
      { type: 'pdf', name: 'Чек-лист_компенсаторики.pdf', size: '2 MB' },
    ],
    targetAudience:
      'Для тех, кто проживает период Саде-Сати или возвращение Сатурна.',
    whatUserGets: [
      'Понимание влияния Сатурна',
      'Методы компенсаторики',
      'Психологическая поддержка',
    ],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-002-1',
        type: 'pdf',
        title: 'Гайд: Транзиты Сатурна',
        description: 'Основной PDF-гайд по прохождению Саде-Сати',
        fileUrl: '/storage/prod002/guide.pdf',
        fileSize: '8 MB',
        accessType: 'download',
        sortOrder: 1,
        status: 'ready',
      },
      {
        id: 'ITEM-002-2',
        type: 'checklist',
        title: 'Чек-лист компенсаторики',
        description: 'Практические рекомендации по смягчению влияния Сатурна',
        fileUrl: '/storage/prod002/checklist.pdf',
        fileSize: '2 MB',
        accessType: 'download',
        sortOrder: 2,
        status: 'ready',
      },
      {
        id: 'ITEM-002-3',
        type: 'bonus',
        title: 'Медитация для Сатурна (аудио)',
        description: 'Аудио-медитация для гармонизации энергии Сатурна',
        fileUrl: '/storage/prod002/meditation.mp3',
        fileSize: '45 MB',
        accessType: 'download',
        sortOrder: 3,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-003',
    title: 'Марафон: Пробуждение Венеры (Отношения и Финансы)',
    description:
      '21-дневный марафон по проработке Венеры в натальной карте. Улучшаем отношения и открываем финансовый поток.',
    type: 'марафон',
    category: 'Проработка планет',
    price: '4 900 ₽',
    priceNum: 4900,
    authorId: 'AST-1',
    authorName: 'Елена Смирнова',
    authorAvatar: 'ЕС',
    status: 'active',
    salesCount: 89,
    revenue: '436 100 ₽',
    rating: 5.0,
    reviewsCount: 45,
    coverColor: 'bg-pink-100',
    createdAt: '15 Мар 2024',
    updatedAt: '20 Мар 2024',
    materials: [
      { type: 'video', name: '21 видеоурок', size: '5 GB' },
      { type: 'pdf', name: 'Дневник марафона', size: '5 MB' },
    ],
    targetAudience: 'Женщины, желающие улучшить личную жизнь и финансы.',
    whatUserGets: ['Проработка блоков', 'Улучшение отношений', 'Рост дохода'],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-003-1',
        type: 'video',
        title: '21 видеоурок марафона',
        description: 'Ежедневные видеоуроки по проработке Венеры',
        fileUrl: '/storage/prod003/videos/',
        fileSize: '5 GB',
        accessType: 'stream',
        sortOrder: 1,
        status: 'ready',
      },
      {
        id: 'ITEM-003-2',
        type: 'pdf',
        title: 'Дневник марафона',
        description: 'Дневник для записи наблюдений и практик',
        fileUrl: '/storage/prod003/diary.pdf',
        fileSize: '5 MB',
        accessType: 'download',
        sortOrder: 2,
        status: 'ready',
      },
      {
        id: 'ITEM-003-3',
        type: 'bonus',
        title: 'Мантры для Венеры (аудио)',
        description: 'Набор мантр для ежедневной практики',
        fileUrl: '/storage/prod003/mantras.mp3',
        fileSize: '30 MB',
        accessType: 'download',
        sortOrder: 3,
        status: 'ready',
      },
      {
        id: 'ITEM-003-4',
        type: 'consultation',
        title: 'Групповой разбор (60 мин)',
        description: 'Участие в групповом разборе карт участников',
        accessType: 'booking',
        sortOrder: 4,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-004',
    title: 'Запись вебинара: Финансовые коды в натальной карте',
    description:
      'Глубокий разбор 2, 8 и 11 домов. Как найти свои источники дохода по натальной карте.',
    type: 'запись',
    category: 'Финансы',
    price: '2 900 ₽',
    priceNum: 2900,
    authorId: 'AST-3',
    authorName: 'Михаил Светлов',
    authorAvatar: 'МС',
    status: 'active',
    salesCount: 210,
    revenue: '609 000 ₽',
    rating: 4.7,
    reviewsCount: 62,
    coverColor: 'bg-emerald-100',
    createdAt: '10 Янв 2024',
    updatedAt: '12 Янв 2024',
    materials: [
      { type: 'video', name: 'Запись_вебинара_Финансы.mp4', size: '1.2 GB' },
      { type: 'pdf', name: 'Презентация.pdf', size: '12 MB' },
    ],
    targetAudience: 'Все, кто хочет увеличить доход с помощью астрологии.',
    whatUserGets: [
      'Понимание своих финансовых домов',
      'Список ниш для заработка',
    ],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-004-1',
        type: 'video',
        title: 'Запись вебинара: Финансовые коды',
        description: 'Полная запись вебинара (2 часа)',
        fileUrl: '/storage/prod004/webinar.mp4',
        fileSize: '1.2 GB',
        accessType: 'stream',
        sortOrder: 1,
        status: 'ready',
      },
      {
        id: 'ITEM-004-2',
        type: 'pdf',
        title: 'Презентация вебинара',
        description: 'Слайды с ключевыми тезисами',
        fileUrl: '/storage/prod004/slides.pdf',
        fileSize: '12 MB',
        accessType: 'download',
        sortOrder: 2,
        status: 'ready',
      },
      {
        id: 'ITEM-004-3',
        type: 'checklist',
        title: 'Чек-лист: Мои финансовые дома',
        description: 'Шаблон для анализа своих финансовых домов',
        fileUrl: '/storage/prod004/checklist.pdf',
        fileSize: '1 MB',
        accessType: 'download',
        sortOrder: 3,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-005',
    title: 'Чек-лист: Совместимость партнёров (Синастрия)',
    description: 'Быстрый алгоритм проверки совместимости по датам рождения.',
    type: 'чек-лист',
    category: 'Отношения',
    price: '490 ₽',
    priceNum: 490,
    authorId: 'AST-2',
    authorName: 'Анна Коваль',
    authorAvatar: 'АК',
    status: 'moderation',
    salesCount: 0,
    revenue: '0 ₽',
    rating: 0,
    reviewsCount: 0,
    coverColor: 'bg-rose-100',
    createdAt: 'Вчера',
    updatedAt: 'Вчера',
    materials: [
      { type: 'pdf', name: 'Чек-лист_Синастрия.pdf', size: '1.5 MB' },
    ],
    targetAudience: 'Для тех, кто хочет быстро проверить совместимость.',
    whatUserGets: ['Алгоритм проверки', 'Описание аспектов'],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-005-1',
        type: 'pdf',
        title: 'Чек-лист: Синастрия',
        description: 'Алгоритм проверки совместимости',
        fileUrl: '/storage/prod005/checklist.pdf',
        fileSize: '1.5 MB',
        accessType: 'download',
        sortOrder: 1,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-006',
    title: 'Видеоурок: Ректификация натальной карты',
    description: 'Пошаговый алгоритм восстановления точного времени рождения.',
    type: 'видеоурок',
    category: 'Профессионалам',
    price: '3 500 ₽',
    priceNum: 3500,
    authorId: 'AST-4',
    authorName: 'Виктор Орлов',
    authorAvatar: 'ВО',
    status: 'hidden',
    salesCount: 45,
    revenue: '157 500 ₽',
    rating: 4.6,
    reviewsCount: 12,
    coverColor: 'bg-blue-100',
    createdAt: '05 Май 2023',
    updatedAt: '10 Окт 2023',
    materials: [
      { type: 'video', name: 'Ректификация_Урок.mp4', size: '800 MB' },
    ],
    targetAudience: 'Практикующие астрологи.',
    whatUserGets: ['Алгоритм ректификации', 'Примеры разборов'],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-006-1',
        type: 'video',
        title: 'Видеоурок: Ректификация',
        description: 'Пошаговый алгоритм восстановления времени рождения',
        fileUrl: '/storage/prod006/lesson.mp4',
        fileSize: '800 MB',
        accessType: 'stream',
        sortOrder: 1,
        status: 'ready',
      },
      {
        id: 'ITEM-006-2',
        type: 'pdf',
        title: 'Таблица событий для ректификации',
        description: 'Шаблон для сбора данных',
        fileUrl: '/storage/prod006/table.pdf',
        fileSize: '500 KB',
        accessType: 'download',
        sortOrder: 2,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-007',
    title: 'Закрытый клуб: Астро-практика',
    description:
      'Ежемесячная подписка на закрытый клуб с разборами карт участников и ответами на вопросы.',
    type: 'закрытый_клуб',
    category: 'Комьюнити',
    price: '1 990 ₽',
    priceNum: 1990,
    authorId: 'AST-1',
    authorName: 'Елена Смирнова',
    authorAvatar: 'ЕС',
    status: 'active',
    salesCount: 120,
    revenue: '238 800 ₽',
    rating: 5.0,
    reviewsCount: 56,
    coverColor: 'bg-purple-100',
    createdAt: '01 Авг 2024',
    updatedAt: '01 Авг 2024',
    materials: [{ type: 'link', name: 'Доступ в закрытый Telegram-канал' }],
    targetAudience: 'Студенты и выпускники курсов.',
    whatUserGets: [
      'Еженедельные эфиры',
      'Разбор сложных случаев',
      'Поддержка коллег',
    ],
    language: 'Русский',
    items: [
      {
        id: 'ITEM-007-1',
        type: 'module',
        title: 'Доступ в закрытый Telegram-канал',
        description: 'Основной канал клуба',
        accessType: 'link',
        sortOrder: 1,
        status: 'ready',
      },
      {
        id: 'ITEM-007-2',
        type: 'video',
        title: 'Еженедельные эфиры (архив)',
        description: 'Записи всех прошедших эфиров',
        accessType: 'stream',
        sortOrder: 2,
        status: 'ready',
      },
      {
        id: 'ITEM-007-3',
        type: 'consultation',
        title: 'Разбор вашей карты в эфире',
        description: 'Возможность задать вопрос в прямом эфире',
        accessType: 'booking',
        sortOrder: 3,
        status: 'ready',
      },
    ],
  },
  {
    id: 'PROD-008',
    title: 'Вебинар: Астрология профессий',
    description: 'Как выбрать профессию по натальной карте. Разбор 10 дома.',
    type: 'вебинар',
    category: 'Карьера',
    price: '1 500 ₽',
    priceNum: 1500,
    authorId: 'AST-5',
    authorName: 'Мария Иванова',
    authorAvatar: 'МИ',
    status: 'draft',
    salesCount: 0,
    revenue: '0 ₽',
    rating: 0,
    reviewsCount: 0,
    coverColor: 'bg-amber-100',
    createdAt: 'Сегодня',
    updatedAt: 'Сегодня',
    materials: [],
    targetAudience: 'Для тех, кто ищет свое призвание.',
    whatUserGets: ['Понимание своих талантов', 'Список подходящих профессий'],
    language: 'Русский',
    items: [],
  },
]

export const initialMarketplaceOrders: MarketplaceOrder[] = [
  {
    id: 'MO-8492',
    productId: 'PROD-002',
    productTitle: 'Гайд: Как пережить транзит Сатурна без потерь',
    productType: 'pdf',
    buyerId: 'USR-101',
    buyerName: 'Ирина В.',
    buyerEmail: 'irina@example.com',
    sellerId: 'AST-2',
    sellerName: 'Анна Коваль',
    price: '1 490 ₽',
    priceNum: 1490,
    platformCommission: 20,
    commissionAmount: '298 ₽',
    sellerPayout: '1 192 ₽',
    status: 'access_granted',
    accessStatus: 'granted',
    createdAt: 'Сегодня, 14:30',
    paymentMethod: 'Банковская карта',
  },
  {
    id: 'MO-8491',
    productId: 'PROD-001',
    productTitle: 'Основы ведической астрологии',
    productType: 'курс',
    buyerId: 'USR-102',
    buyerName: 'Ольга М.',
    buyerEmail: 'olga@example.com',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    price: '15 000 ₽',
    priceNum: 15000,
    platformCommission: 15,
    commissionAmount: '2 250 ₽',
    sellerPayout: '12 750 ₽',
    status: 'access_granted',
    accessStatus: 'granted',
    createdAt: 'Сегодня, 11:15',
    paymentMethod: 'СБП',
  },
  {
    id: 'MO-8490',
    productId: 'PROD-004',
    productTitle: 'Запись вебинара: Финансовые коды',
    productType: 'запись',
    buyerId: 'USR-103',
    buyerName: 'Дмитрий К.',
    buyerEmail: 'dmitry@example.com',
    sellerId: 'AST-3',
    sellerName: 'Михаил Светлов',
    price: '2 900 ₽',
    priceNum: 2900,
    platformCommission: 20,
    commissionAmount: '580 ₽',
    sellerPayout: '2 320 ₽',
    status: 'pending_payment',
    accessStatus: 'pending',
    createdAt: 'Сегодня, 09:45',
    paymentMethod: 'Банковская карта',
  },
  {
    id: 'MO-8489',
    productId: 'PROD-003',
    productTitle: 'Марафон: Пробуждение Венеры',
    productType: 'марафон',
    buyerId: 'USR-104',
    buyerName: 'Светлана П.',
    buyerEmail: 'sveta@example.com',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    price: '4 900 ₽',
    priceNum: 4900,
    platformCommission: 20,
    commissionAmount: '980 ₽',
    sellerPayout: '3 920 ₽',
    status: 'refunded',
    accessStatus: 'revoked',
    createdAt: 'Вчера, 18:20',
    paymentMethod: 'Банковская карта',
  },
  {
    id: 'MO-8488',
    productId: 'PROD-007',
    productTitle: 'Закрытый клуб: Астро-практика',
    productType: 'закрытый_клуб',
    buyerId: 'USR-105',
    buyerName: 'Екатерина Н.',
    buyerEmail: 'katya@example.com',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    price: '1 990 ₽',
    priceNum: 1990,
    platformCommission: 10,
    commissionAmount: '199 ₽',
    sellerPayout: '1 791 ₽',
    status: 'paid',
    accessStatus: 'pending',
    createdAt: 'Вчера, 15:10',
    paymentMethod: 'ЮKassa',
  },
  {
    id: 'MO-8487',
    productId: 'PROD-006',
    productTitle: 'Видеоурок: Ректификация натальной карты',
    productType: 'видеоурок',
    buyerId: 'USR-106',
    buyerName: 'Алексей Т.',
    buyerEmail: 'alex@example.com',
    sellerId: 'AST-4',
    sellerName: 'Виктор Орлов',
    price: '3 500 ₽',
    priceNum: 3500,
    platformCommission: 20,
    commissionAmount: '700 ₽',
    sellerPayout: '2 800 ₽',
    status: 'disputed',
    accessStatus: 'granted',
    createdAt: 'Вчера, 12:05',
    paymentMethod: 'Банковская карта',
  },
  {
    id: 'MO-8486',
    productId: 'PROD-002',
    productTitle: 'Гайд: Как пережить транзит Сатурна без потерь',
    productType: 'pdf',
    buyerId: 'USR-107',
    buyerName: 'Марина Л.',
    buyerEmail: 'marina@example.com',
    sellerId: 'AST-2',
    sellerName: 'Анна Коваль',
    price: '1 490 ₽',
    priceNum: 1490,
    platformCommission: 20,
    commissionAmount: '298 ₽',
    sellerPayout: '1 192 ₽',
    status: 'access_granted',
    accessStatus: 'granted',
    createdAt: '2 дня назад',
    paymentMethod: 'СБП',
  },
  {
    id: 'MO-8485',
    productId: 'PROD-001',
    productTitle: 'Основы ведической астрологии',
    productType: 'курс',
    buyerId: 'USR-108',
    buyerName: 'Наталья К.',
    buyerEmail: 'natalia@example.com',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    price: '15 000 ₽',
    priceNum: 15000,
    platformCommission: 15,
    commissionAmount: '2 250 ₽',
    sellerPayout: '12 750 ₽',
    status: 'disputed',
    accessStatus: 'granted',
    createdAt: '3 дня назад',
    paymentMethod: 'Банковская карта',
  },
]

export const initialMarketplaceSellers: MarketplaceSeller[] = [
  {
    id: 'AST-1',
    name: 'Елена Смирнова',
    email: 'elena.astro@example.com',
    productsCount: 4,
    totalSales: 450,
    totalRevenue: '3 150 000 ₽',
    balance: '45 000 ₽',
    paidOut: '2 600 000 ₽',
    rating: 4.9,
    complaintsCount: 1,
    status: 'active',
  },
  {
    id: 'AST-2',
    name: 'Анна Коваль',
    email: 'anna.k@example.com',
    productsCount: 2,
    totalSales: 356,
    totalRevenue: '530 440 ₽',
    balance: '12 500 ₽',
    paidOut: '411 000 ₽',
    rating: 4.8,
    complaintsCount: 0,
    status: 'active',
  },
  {
    id: 'AST-3',
    name: 'Михаил Светлов',
    email: 'mihail.s@example.com',
    productsCount: 1,
    totalSales: 210,
    totalRevenue: '609 000 ₽',
    balance: '0 ₽',
    paidOut: '487 200 ₽',
    rating: 4.7,
    complaintsCount: 2,
    status: 'active',
  },
  {
    id: 'AST-4',
    name: 'Виктор Орлов',
    email: 'viktor.o@example.com',
    productsCount: 1,
    totalSales: 45,
    totalRevenue: '157 500 ₽',
    balance: '2 800 ₽',
    paidOut: '150 000 ₽',
    rating: 4.6,
    complaintsCount: 3,
    status: 'restricted',
  },
  {
    id: 'AST-5',
    name: 'Мария Иванова',
    email: 'maria.i@example.com',
    productsCount: 1,
    totalSales: 0,
    totalRevenue: '0 ₽',
    balance: '0 ₽',
    paidOut: '0 ₽',
    rating: 0,
    complaintsCount: 0,
    status: 'active',
  },
]

export const initialMarketplacePayouts: MarketplacePayout[] = [
  {
    id: 'PO-1042',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    amount: '45 000 ₽',
    commission: '0 ₽',
    status: 'pending',
    createdAt: 'Сегодня, 10:00',
  },
  {
    id: 'PO-1041',
    sellerId: 'AST-2',
    sellerName: 'Анна Коваль',
    amount: '25 000 ₽',
    commission: '0 ₽',
    status: 'paid_out',
    createdAt: 'Вчера, 15:30',
    transactionId: 'TRX-998877',
  },
  {
    id: 'PO-1040',
    sellerId: 'AST-3',
    sellerName: 'Михаил Светлов',
    amount: '18 500 ₽',
    commission: '0 ₽',
    status: 'paid_out',
    createdAt: '12 Окт 2024',
    transactionId: 'TRX-998876',
  },
  {
    id: 'PO-1039',
    sellerId: 'AST-4',
    sellerName: 'Виктор Орлов',
    amount: '5 000 ₽',
    commission: '0 ₽',
    status: 'failed',
    createdAt: '10 Окт 2024',
  },
  {
    id: 'PO-1038',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    amount: '120 000 ₽',
    commission: '0 ₽',
    status: 'paid_out',
    createdAt: '01 Окт 2024',
    transactionId: 'TRX-998800',
  },
]

export const initialMarketplaceDisputes: MarketplaceDispute[] = [
  {
    id: 'DSP-1001',
    orderId: 'MO-8487',
    productId: 'PROD-006',
    productTitle: 'Видеоурок: Ректификация натальной карты',
    buyerId: 'USR-106',
    buyerName: 'Алексей Т.',
    sellerId: 'AST-4',
    sellerName: 'Виктор Орлов',
    reason:
      'Видео не воспроизводится, выдает ошибку 404. Продавец не отвечает.',
    status: 'open',
    createdAt: 'Сегодня, 09:15',
    amount: '3 500 ₽',
  },
  {
    id: 'DSP-1002',
    orderId: 'MO-8485',
    productId: 'PROD-001',
    productTitle: 'Основы ведической астрологии',
    buyerId: 'USR-108',
    buyerName: 'Наталья К.',
    sellerId: 'AST-1',
    sellerName: 'Елена Смирнова',
    reason:
      'Курс не соответствует описанию. Нет заявленного модуля по совместимости.',
    status: 'in_review',
    createdAt: 'Вчера, 14:20',
    amount: '15 000 ₽',
  },
  {
    id: 'DSP-1003',
    orderId: 'MO-8400',
    productId: 'PROD-002',
    productTitle: 'Гайд: Как пережить транзит Сатурна без потерь',
    buyerId: 'USR-099',
    buyerName: 'Светлана М.',
    sellerId: 'AST-2',
    sellerName: 'Анна Коваль',
    reason: 'Случайно купила дважды.',
    status: 'resolved_buyer',
    createdAt: '10 Окт 2024',
    resolvedAt: '11 Окт 2024',
    adminComment: 'Оформлен возврат средств за дублирующий заказ.',
    amount: '1 490 ₽',
  },
  {
    id: 'DSP-1004',
    orderId: 'MO-8350',
    productId: 'PROD-004',
    productTitle: 'Запись вебинара: Финансовые коды',
    buyerId: 'USR-085',
    buyerName: 'Игорь В.',
    sellerId: 'AST-3',
    sellerName: 'Михаил Светлов',
    reason: 'Не получил ссылку на вебинар после оплаты.',
    status: 'resolved_seller',
    createdAt: '05 Окт 2024',
    resolvedAt: '05 Окт 2024',
    adminComment:
      'Ссылка была в спаме. Покупатель нашел письмо, претензий не имеет.',
    amount: '2 900 ₽',
  },
]

export const initialMarketplaceReviews: MarketplaceReview[] = [
  {
    id: 'REV-001',
    productId: 'PROD-001',
    productTitle: 'Основы ведической астрологии',
    buyerId: 'USR-102',
    buyerName: 'Ольга М.',
    rating: 5,
    text: 'Потрясающий курс! Елена объясняет всё очень доступно. Уже начала практиковать на друзьях.',
    createdAt: 'Сегодня, 12:00',
    status: 'published',
  },
  {
    id: 'REV-002',
    productId: 'PROD-002',
    productTitle: 'Гайд: Как пережить транзит Сатурна без потерь',
    buyerId: 'USR-101',
    buyerName: 'Ирина В.',
    rating: 4,
    text: 'Хороший гайд, много полезной информации. Но хотелось бы больше примеров.',
    createdAt: 'Вчера, 18:45',
    status: 'published',
  },
  {
    id: 'REV-003',
    productId: 'PROD-003',
    productTitle: 'Марафон: Пробуждение Венеры',
    buyerId: 'USR-090',
    buyerName: 'Анна С.',
    rating: 5,
    text: 'Марафон изменил мою жизнь! Отношения с мужем вышли на новый уровень.',
    createdAt: '12 Окт 2024',
    status: 'published',
  },
  {
    id: 'REV-004',
    productId: 'PROD-004',
    productTitle: 'Запись вебинара: Финансовые коды',
    buyerId: 'USR-088',
    buyerName: 'Максим П.',
    rating: 5,
    text: 'Михаил дает четкие инструкции. Понял, почему не шли деньги в бизнесе.',
    createdAt: '10 Окт 2024',
    status: 'published',
  },
  {
    id: 'REV-005',
    productId: 'PROD-006',
    productTitle: 'Видеоурок: Ректификация натальной карты',
    buyerId: 'USR-106',
    buyerName: 'Алексей Т.',
    rating: 1,
    text: 'Видео не работает! Верните деньги!',
    createdAt: 'Вчера, 09:00',
    status: 'moderation',
  },
  {
    id: 'REV-006',
    productId: 'PROD-001',
    productTitle: 'Основы ведической астрологии',
    buyerId: 'USR-108',
    buyerName: 'Наталья К.',
    rating: 2,
    text: 'Курс не оправдал ожиданий. Слишком много воды.',
    createdAt: '3 дня назад',
    status: 'published',
  },
  {
    id: 'REV-007',
    productId: 'PROD-007',
    productTitle: 'Закрытый клуб: Астро-практика',
    buyerId: 'USR-070',
    buyerName: 'Елена Д.',
    rating: 5,
    text: 'Лучшее комьюнити! Очень поддерживающая атмосфера.',
    createdAt: '05 Окт 2024',
    status: 'published',
  },
  {
    id: 'REV-008',
    productId: 'PROD-002',
    productTitle: 'Гайд: Как пережить транзит Сатурна без потерь',
    buyerId: 'USR-065',
    buyerName: 'Дарья Ф.',
    rating: 5,
    text: 'Спасение в период Саде-Сати. Чек-лист компенсаторики просто супер.',
    createdAt: '01 Окт 2024',
    status: 'published',
  },
]

export const initialMarketplaceNotifications: MarketplaceNotificationLog[] = [
  {
    id: 'NOT-001',
    type: 'product_approved',
    recipientType: 'astrologer',
    recipientName: 'Елена Смирнова',
    message:
      'Ваш товар "Закрытый клуб: Астро-практика" успешно прошел модерацию и опубликован.',
    status: 'sent',
    sentAt: 'Сегодня, 10:05',
    channel: 'internal',
  },
  {
    id: 'NOT-002',
    type: 'order_paid',
    recipientType: 'user',
    recipientName: 'Ирина В.',
    message:
      'Оплата заказа MO-8492 прошла успешно. Доступ к материалам открыт.',
    status: 'sent',
    sentAt: 'Сегодня, 14:31',
    channel: 'email',
  },
  {
    id: 'NOT-003',
    type: 'order_paid',
    recipientType: 'astrologer',
    recipientName: 'Анна Коваль',
    message:
      'У вас новая продажа! Товар "Гайд: Как пережить транзит Сатурна без потерь" куплен.',
    status: 'sent',
    sentAt: 'Сегодня, 14:31',
    channel: 'internal',
  },
  {
    id: 'NOT-004',
    type: 'payout_sent',
    recipientType: 'astrologer',
    recipientName: 'Анна Коваль',
    message: 'Выплата в размере 25 000 ₽ успешно отправлена на ваши реквизиты.',
    status: 'sent',
    sentAt: 'Вчера, 15:35',
    channel: 'email',
  },
  {
    id: 'NOT-005',
    type: 'dispute_opened',
    recipientType: 'admin',
    recipientName: 'Администратор',
    message: 'Открыт новый спор DSP-1001 по заказу MO-8487.',
    status: 'sent',
    sentAt: 'Сегодня, 09:16',
    channel: 'internal',
  },
  {
    id: 'NOT-006',
    type: 'dispute_opened',
    recipientType: 'astrologer',
    recipientName: 'Виктор Орлов',
    message:
      'Покупатель открыл спор по вашему товару "Видеоурок: Ректификация натальной карты". Пожалуйста, ответьте в течение 24 часов.',
    status: 'sent',
    sentAt: 'Сегодня, 09:16',
    channel: 'email',
  },
  {
    id: 'NOT-007',
    type: 'product_rejected',
    recipientType: 'astrologer',
    recipientName: 'Мария Иванова',
    message:
      'Ваш товар "Вебинар: Астрология профессий" отклонен модератором. Причина: отсутствует программа вебинара.',
    status: 'sent',
    sentAt: 'Вчера, 11:00',
    channel: 'internal',
  },
  {
    id: 'NOT-008',
    type: 'payout_failed',
    recipientType: 'astrologer',
    recipientName: 'Виктор Орлов',
    message:
      'Ошибка при отправке выплаты 5 000 ₽. Пожалуйста, проверьте реквизиты.',
    status: 'failed',
    sentAt: '10 Окт 2024',
    channel: 'email',
  },
  {
    id: 'NOT-009',
    type: 'review_published',
    recipientType: 'astrologer',
    recipientName: 'Елена Смирнова',
    message:
      'Новый отзыв (5 звезд) на ваш товар "Основы ведической астрологии".',
    status: 'sent',
    sentAt: 'Сегодня, 12:05',
    channel: 'internal',
  },
  {
    id: 'NOT-010',
    type: 'order_refunded',
    recipientType: 'user',
    recipientName: 'Светлана П.',
    message:
      'Возврат средств по заказу MO-8489 успешно оформлен. Деньги поступят на карту в течение 3-5 дней.',
    status: 'sent',
    sentAt: 'Вчера, 18:25',
    channel: 'email',
  },
]
